<template>
  <div v-if="show" class="remaining-animals-prompt">
    <div class="prompt-overlay" @click="handleCancel"></div>
    <div class="prompt-dialog">
      <div class="prompt-header">
        <div class="prompt-icon">
          <i class="pi pi-check-circle"></i>
        </div>
        <h3 class="prompt-title">All Expected Animals Scanned</h3>
      </div>

      <div class="prompt-content">
        <p class="prompt-message">
          You have successfully scanned all {{ expectedCount }} expected animals. Would you like to
          scan any additional animals in this cage?
        </p>
      </div>

      <div class="prompt-actions">
        <button type="button" class="btn btn-secondary" @click="handleContinueScanning">
          Scan Additional Animals
        </button>
        <button type="button" class="btn btn-primary" @click="handleComplete">
          Complete Audit
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';

interface Props {
  show: boolean;
  expectedCount: number;
}

interface Emits {
  (e: 'continue-scanning'): void;
  (e: 'complete'): void;
  (e: 'cancel'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

function handleContinueScanning(): void {
  emit('continue-scanning');
}

function handleComplete(): void {
  emit('complete');
}

function handleCancel(): void {
  emit('cancel');
}

// Prevent body scroll when prompt is shown
watch(
  () => props.show,
  (isShowing) => {
    if (isShowing) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  },
);
</script>

<style scoped>
.remaining-animals-prompt {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.prompt-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.prompt-dialog {
  position: relative;
  background: white;
  border-radius: 1rem;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 100%;
  padding: 2rem;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.prompt-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.prompt-icon {
  width: 4rem;
  height: 4rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 2rem;
  color: white;
}

.prompt-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.prompt-content {
  margin-bottom: 2rem;
}

.prompt-message {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  text-align: center;
}

.prompt-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.btn-primary:active {
  transform: translateY(0);
}

@media (max-width: 768px) {
  .prompt-dialog {
    padding: 1.5rem;
    max-width: 100%;
  }

  .prompt-icon {
    width: 3rem;
    height: 3rem;
    font-size: 1.5rem;
  }

  .prompt-title {
    font-size: 1.25rem;
  }

  .prompt-message {
    font-size: 0.9375rem;
  }

  .prompt-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }
}
</style>
